import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { Label } from "@/components/ui/base/label";
import { MccCombobox } from "@/components/ui/MccCombobox";
import { selectClass } from "@/components/ui/styles";
import {
  getMaxBirthDateString,
  getMinBirthDateString,
} from "@cpos/shared/src/validation/identity";

export function BusinessStep() {
  const { provisioningBloc } = useAdminApp();
  const [state] = useStateMachineValue(provisioningBloc);
  const { formData, fieldErrors } = state;

  return (
    <div>
      <h2 className="border-border text-foreground mb-6 border-b pb-4 text-lg font-semibold">
        Business Details
      </h2>
      <div className="grid grid-cols-2 gap-5">
        <div className="col-span-2">
          <Label htmlFor="businessName">Business Name</Label>
          <Input
            id="businessName"
            value={formData.businessName ?? ""}
            onChange={(e) =>
              provisioningBloc.updateField("businessName", e.target.value)
            }
          />
          {fieldErrors.businessName && (
            <p className="mt-1 text-xs text-red-600">
              {fieldErrors.businessName}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="businessCountry">Country</Label>
          <select
            id="businessCountry"
            className={selectClass()}
            value={formData.businessCountry ?? "CA"}
            onChange={(e) => {
              const newCountry = e.target.value as "CA" | "US";
              provisioningBloc.updateField("businessCountry", newCountry);
              if (newCountry === "US") {
                provisioningBloc.updateField("taxId", undefined);
              } else {
                provisioningBloc.updateField("ssnLast4", undefined);
              }
            }}
          >
            <option value="CA">Canada</option>
            <option value="US">United States</option>
          </select>
        </div>
        <div>
          <Label htmlFor="businessType">Business Type</Label>
          <select
            id="businessType"
            className={selectClass()}
            value={formData.businessType ?? "individual"}
            onChange={(e) =>
              provisioningBloc.updateField(
                "businessType",
                e.target.value as "individual" | "company" | "non_profit",
              )
            }
          >
            <option value="individual">Individual</option>
            <option value="company">Company</option>
            <option value="non_profit">Non-profit</option>
          </select>
        </div>
        <div>
          <Label htmlFor="businessCategoryCode">Category Code (MCC)</Label>
          <MccCombobox
            id="businessCategoryCode"
            value={formData.businessCategoryCode ?? ""}
            onChange={(code) =>
              provisioningBloc.updateField("businessCategoryCode", code)
            }
            error={fieldErrors.businessCategoryCode}
          />
          {fieldErrors.businessCategoryCode && (
            <p className="mt-1 text-xs text-red-600">
              {fieldErrors.businessCategoryCode}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="birthDate">Owner Date of Birth</Label>
          <Input
            id="birthDate"
            type="date"
            min={getMinBirthDateString()}
            max={getMaxBirthDateString()}
            value={formData.birthDate ?? ""}
            onChange={(e) =>
              provisioningBloc.updateField("birthDate", e.target.value)
            }
          />
          {fieldErrors.birthDate && (
            <p className="mt-1 text-xs text-red-600">{fieldErrors.birthDate}</p>
          )}
        </div>
        {formData.businessCountry === "CA" && (
          <div>
            <Label htmlFor="taxId">Business Number (BN)</Label>
            <Input
              id="taxId"
              placeholder="123456789"
              value={formData.taxId ?? ""}
              onChange={(e) =>
                provisioningBloc.updateField("taxId", e.target.value)
              }
            />
          </div>
        )}
        {formData.businessCountry === "US" && (
          <div>
            <Label htmlFor="ssnLast4">SSN Last 4</Label>
            <Input
              id="ssnLast4"
              maxLength={4}
              placeholder="0000"
              value={formData.ssnLast4 ?? ""}
              onChange={(e) =>
                provisioningBloc.updateField("ssnLast4", e.target.value)
              }
            />
            {fieldErrors.ssnLast4 && (
              <p className="mt-1 text-xs text-red-600">
                {fieldErrors.ssnLast4}
              </p>
            )}
          </div>
        )}
        <div>
          <Label htmlFor="businessUrl">Business URL (optional)</Label>
          <Input
            id="businessUrl"
            placeholder="https://example.com"
            value={formData.businessUrl ?? ""}
            onChange={(e) =>
              provisioningBloc.updateField("businessUrl", e.target.value)
            }
          />
        </div>
        <div>
          <Label htmlFor="supportPhone">Support Phone (optional)</Label>
          <Input
            id="supportPhone"
            placeholder="+1 (555) 000-0000"
            value={formData.supportPhone ?? ""}
            onChange={(e) =>
              provisioningBloc.updateField("supportPhone", e.target.value)
            }
          />
        </div>
      </div>
      <div className="border-border mt-8 flex justify-between border-t pt-6">
        <Button variant="outline" onClick={() => provisioningBloc.prevStep()}>
          ← Back
        </Button>
        <Button onClick={() => provisioningBloc.nextStep()}>Next →</Button>
      </div>
    </div>
  );
}
